import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  lang: string;
}

const langPathMap: Record<string, string> = {
  en: '/en',
  fr: '/fr',
  de: '/de',
  da: '/da',
};

const langLabelMap: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  de: 'DE',
  da: 'DA',
};

const allLangs = ['en', 'fr', 'de', 'da'];

export default function Navbar({ lang }: NavbarProps) {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const langRef = useRef<HTMLDivElement>(null);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchLang = (newLang: string) => {
    const currentPath = location.pathname;
    const rest = currentPath.replace(/^\/(en|fr|de|da)/, '') || '';
    const newPath = `/${newLang}${rest}`;
    window.location.href = newPath;
  };

  const isHomePage = ['/', '/en', '/fr', '/de', '/da'].includes(location.pathname);
  const homePath = langPathMap[lang] || '/en';

  const scrollToSection = (sectionId: string) => {
    if (isHomePage) {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`${homePath}#${sectionId}`);
    }
    setMobileOpen(false);
  };

  const categories = [
    { label: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', slug: 'shoes', icon: 'ri-footprint-line' },
    { label: 'Hoodies / Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', slug: 'hoodies', icon: 'ri-t-shirt-line' },
    { label: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', slug: 'tshirts', icon: 'ri-t-shirt-air-line' },
    { label: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', slug: 'jackets', icon: 'ri-shirt-line' },
    { label: 'Pants / Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', slug: 'pants', icon: 'ri-scissors-cut-line' },
    { label: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html', slug: 'headwear', icon: 'ri-base-station-line' },
    { label: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html', slug: 'sets', icon: 'ri-stack-line' },
    { label: 'Underwear / Underpants', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html', slug: 'underwear', icon: 'ri-shirt-line' },
    { label: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html', slug: 'jersey', icon: 'ri-t-shirt-2-line' },
    { label: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', slug: 'accessories', icon: 'ri-vip-diamond-line' },
    { label: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', slug: 'bags', icon: 'ri-shopping-bag-3-line' },
    { label: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', slug: 'belts', icon: 'ri-circle-line' },
    { label: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', slug: 'perfumes', icon: 'ri-drop-line' },
    { label: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', slug: 'other', icon: 'ri-more-line' },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full bg-white border-b-2 border-background-200">
      <div className="section-container">
        <div className="section-inner flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to={homePath} className="flex items-center gap-2 flex-shrink-0">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              {t('nav_brand')}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to={homePath}
              className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-900 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {t('nav_home')}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-900 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-background-200 rounded-lg shadow-lg overflow-hidden animate-slide-down z-50">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                        onClick={() => setCatOpen(false)}
                      >
                        <i className={`${cat.icon} text-primary-500`} />
                        {cat.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('guides')}
              className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-900 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav_guides')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-900 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav_faq')}
            </button>
          </div>

          {/* Right side: Language + External CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors border border-background-200 cursor-pointer"
              >
                <i className="ri-global-line" />
                <span>{langLabelMap[lang] || 'EN'}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-background-200 rounded-lg shadow-lg overflow-hidden animate-slide-down z-50">
                  {allLangs.map((l) => (
                    <button
                      key={l}
                      onClick={() => { switchLang(l); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                        l === lang
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-800 hover:bg-background-100'
                      }`}
                    >
                      {t(`lang_${l}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary text-sm px-4 py-2"
            >
              {t('ext_btn_main')}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-white animate-fade-in overflow-y-auto max-h-[calc(100vh-4rem)]">
          <div className="section-container py-4">
            {/* Primary links */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              <Link
                to={homePath}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-background-100 text-foreground-900 font-semibold text-sm hover:bg-background-200 transition-colors"
              >
                <i className="ri-home-5-line text-lg" />
                {t('nav_home')}
              </Link>
              <button
                onClick={() => scrollToSection('guides')}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-background-100 text-foreground-900 font-semibold text-sm hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line text-lg" />
                {t('nav_guides')}
              </button>
            </div>

            {/* Categories grid */}
            <div className="mb-4">
              <div className="px-1 pb-3 flex items-center gap-2 text-sm font-bold text-foreground-950 uppercase tracking-wide">
                <i className="ri-grid-line text-primary-500" />
                {t('nav_categories')}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-800 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <i className={`${cat.icon} text-primary-500 text-base flex-shrink-0`} />
                    <span className="truncate">{cat.label}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* FAQ link */}
            <button
              onClick={() => scrollToSection('faq')}
              className="w-full flex items-center gap-2 px-4 py-3 rounded-lg bg-background-100 text-foreground-900 font-semibold text-sm hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-question-line text-lg" />
              {t('nav_faq')}
            </button>

            {/* Language switcher */}
            <div className="pt-4 mt-4 border-t border-background-200">
              <div className="px-1 pb-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                {t('lang_switch')}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {allLangs.map((l) => (
                  <button
                    key={l}
                    onClick={() => { switchLang(l); setMobileOpen(false); }}
                    className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer ${
                      l === lang
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-800 hover:bg-background-200'
                    }`}
                  >
                    {langLabelMap[l]}
                  </button>
                ))}
              </div>
            </div>

            {/* External CTA */}
            <div className="pt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary w-full text-sm py-3"
              >
                {t('ext_btn_main')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}